import type { SystemStoreDetail } from "./types";

/**
 * Pull the store out of whatever the endpoint wrapped it in.
 *
 * `GET /system/stores/{id}` and `PATCH /system/stores/{id}` are both described in
 * the API docs by prose — "Store detail including…", "The updated store." — with
 * no response schema, so neither documents whether the store arrives bare or
 * inside this app's usual `{ data }` envelope. Accepting either in ONE place is
 * the point: the query and the mutation write to the same cache key, so if they
 * disagreed about the envelope a save would replace a real store with a wrapper
 * object and every fact on the page would go blank.
 *
 * Returns `undefined` for anything that is not an object — a 204, an empty body,
 * a bare `true`. That keeps "no store came back" distinguishable from "a store
 * with no fields", which is the difference between leaving the cache alone and
 * wiping it.
 */
export function unwrapStore(res: unknown): SystemStoreDetail | undefined {
  if (!res || typeof res !== "object" || Array.isArray(res)) return undefined;

  // A `data` key means this IS the envelope — no store field is named `data` in
  // the DTO. So an envelope carrying `data: null` is the API saying "no store",
  // and must not fall through to caching the wrapper itself as if it were one:
  // that object has none of the store's fields, so every fact on the page would
  // render as absent rather than as an error.
  if ("data" in (res as Record<string, unknown>)) {
    const inner = (res as { data?: unknown }).data;
    return inner && typeof inner === "object" && !Array.isArray(inner)
      ? (inner as SystemStoreDetail)
      : undefined;
  }

  return res as SystemStoreDetail;
}
